import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EmployeeProfile } from 'components/ui';
import { employeesService } from 'services/employees.service';
import { Employee } from 'types/employee';

export const PublicEmployeePage: React.FC = () => {
  const { key: routeKey } = useParams<{ key: string }>();
  const key = useMemo(() => {
    if (!routeKey) return '';
    try {
      return decodeURIComponent(routeKey.trim());
    } catch {
      return routeKey.trim();
    }
  }, [routeKey]);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [licenseExpired, setLicenseExpired] = useState<boolean>(false);
  const [isDemo, setIsDemo] = useState<boolean>(false);

  useEffect(() => {
    const loadEmployee = async () => {
      if (!key) {
        setError('Profile key is required');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        setLicenseExpired(false);
        setIsDemo(false);
        const emp = await employeesService.getByPublicKey(key);
        setEmployee(emp); 
        setLicenseExpired(Boolean((emp as any)?.licenseExpired));
        setIsDemo(Boolean((emp as any)?.isDemo));
      } catch (e: any) {
        const status = e?.response?.status;
        const code =
          e?.response?.data?.code ||
          e?.response?.data?.errorCode ||
          e?.code;
        const message =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          'Employee not found or unable to load profile';

        const messageLc = String(message || '').toLowerCase();
        const looksLikeExpired =
          messageLc.includes('license') &&
          (messageLc.includes('expired') || messageLc.includes('missing'));

        if (status === 403 || String(code).toUpperCase() === 'LICENSE_EXPIRED' || looksLikeExpired) {
          // Render empty profile with a badge
          setLicenseExpired(true);
          setIsDemo(false);
          setError(null);
          setEmployee(null);
        } else {
          setError(message);
          setEmployee(null);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadEmployee();
  }, [key]); 
  
  return (
    <EmployeeProfile
      employee={employee}
      variant="public"
      defaultSectionsOpen={false}
      isLoading={isLoading}
      error={error}
      licenseExpired={licenseExpired}
      isDemo={isDemo}
    />
  );
};

export default PublicEmployeePage;


